import React, { useMemo } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { BookCopy, PlusCircle, AlertTriangle, Info, Share, Trash2 } from 'lucide-react';

import SettingsCard from '../SettingsCard';
import { ListToolbar } from '../../Common/ListToolbar';
import { EmptyState } from '../../Common/EmptyState';
import useListControls from '../../../../hooks/useListControls';

// Bokföringsmallar — its own settings route since the Bokföring split.
//
// This was the single worst surface in Settings: 95 templates rendered as a
// two-column card grid with the full description on every card, no search, no
// filter, no paging — 15 307px, twenty screens, inside a page that was already
// thirty-five. It now uses the same ListToolbar/EmptyState the other seventeen
// list pages use, and rows instead of cards.

const OWN = 'own';
const SHARED = 'shared';

const isOwn = (t) => t.source === 'tenant';
const isShared = (t) => t.source === 'shared' || t.source === 'pending';

const SCOPE_OPTIONS = [
  { value: OWN, label: 'Mina mallar' },
  { value: SHARED, label: 'Delade mallar' },
];

const TemplatesSection = ({
  templates,
  navigateToTemplateEditor,
  handleSubmitForSharing,
  handleDeleteTemplate,
}) => {
  const ownCount = templates.filter(isOwn).length;
  const sharedCount = templates.filter(isShared).length;

  // Land on whichever scope actually has templates. Without this a tenant with
  // no private mallar opens an empty list and concludes the system is broken
  // while 95 shared ones sit one click away.
  const { search, setSearch, debouncedSearch, filter: scope, setFilter: setScope, isFiltered, reset } =
    useListControls({
      defaultFilter: ownCount === 0 && sharedCount > 0 ? SHARED : OWN,
      defaultSort: null,
      searchKey: 'q',
      filterKey: 'typ',
    });

  const visible = useMemo(() => {
    const inScope = templates.filter(scope === SHARED ? isShared : isOwn);
    const q = debouncedSearch.toLowerCase();
    if (!q) return inScope;
    return inScope.filter((t) =>
      `${t.template_name || ''} ${t.description || ''}`.toLowerCase().includes(q)
    );
  }, [templates, scope, debouncedSearch]);

  return (
    <SettingsCard
      id="mallar"
      icon={BookCopy}
      title="Bokföringsmallar"
      subtitle="Skapa och hantera mallar för återkommande bokföringstransaktioner"
      actions={
        <Button
          variant="primary"
          onClick={() => navigateToTemplateEditor()}
          className="d-flex align-items-center gap-2"
        >
          <PlusCircle size={16} />
          Skapa ny mall
        </Button>
      }
    >
      <ListToolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Sök bland mallar…',
        }}
      >
        <div className="ll-segmented" role="group" aria-label="Malltyp">
          {SCOPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`ll-segmented__item${scope === opt.value ? ' is-active' : ''}`}
              onClick={() => setScope(opt.value)}
              aria-pressed={scope === opt.value}
            >
              {opt.label}
              <span className="ll-segmented__count">
                {opt.value === OWN ? ownCount : sharedCount}
              </span>
            </button>
          ))}
        </div>
      </ListToolbar>

      {visible.length === 0 && isFiltered && (
        <EmptyState
          variant="no-matches"
          query={search}
          actions={<Button variant="link" onClick={reset}>Rensa filter</Button>}
        />
      )}

      {visible.length === 0 && !isFiltered && (
        <EmptyState
          icon={BookCopy}
          title={scope === SHARED ? 'Inga delade mallar tillgängliga' : 'Inga egna mallar än'}
          description={scope === SHARED
            ? 'Delade mallar från andra företag visas här.'
            : 'Skapa mallar för att effektivisera återkommande bokföringstransaktioner.'}
          actions={scope === OWN ? (
            <Button variant="primary" onClick={() => navigateToTemplateEditor()}>
              <PlusCircle className="me-2" size={16} />
              Skapa första mallen
            </Button>
          ) : null}
        />
      )}

      {visible.length > 0 && (
        <ul className="settings-rows">
          {visible.map((template) => (
            <li key={template.template_id} className="settings-row">
              <div className="settings-row__text">
                <span className="settings-row__name">
                  {template.template_name}
                  {template.approval_status === 'pending_approval' && (
                    <Badge bg="warning" className="ms-2">Väntar på godkännande</Badge>
                  )}
                  {template.approval_status === 'rejected' && (
                    <Badge bg="danger" className="ms-2">Nekad</Badge>
                  )}
                </span>
                {/* Descriptions are paragraphs — clamped to two lines so 95 of
                    them stay a list you can scan rather than a wall. */}
                <p className="settings-row__desc">{template.description}</p>
                {template.rejection_reason && (
                  <p className="settings-row__alert">
                    <AlertTriangle size={14} className="me-1" />
                    <strong>Anledning:</strong> {template.rejection_reason}
                  </p>
                )}
              </div>

              <div className="settings-row__actions">
                {/* Visa detaljer — the single edit entry point for every
                    template (private or system). The editor routes save to PUT
                    for tenant rows and POST-as-private for system/shared rows;
                    users see one consistent path. */}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigateToTemplateEditor(template.template_id)}
                >
                  <Info size={14} className="me-1" />
                  Visa detaljer
                </Button>

                {isOwn(template) && !template.shared_template_id && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleSubmitForSharing(template.template_id)}
                  >
                    <Share size={14} className="me-1" />
                    Dela
                  </Button>
                )}

                {isOwn(template) && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteTemplate(template.template_id, template.template_name)}
                  >
                    <Trash2 size={14} className="me-1" />
                    Ta bort
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </SettingsCard>
  );
};

export default TemplatesSection;
